/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormField, FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import copy from 'copy-to-clipboard'
import { Form, FormikProps, withFormik } from 'formik'
import {
  DomainName,
  PromoPage,
  PromoPageFetch,
  SGLLInkGenerator,
} from 'types/Marketing/SGLLinkGenerator.types'
import { mainWebsiteLanguages } from 'utils/languages'

import { TickmillCompanyDto } from '../../../api/Identity/Types/Company'

const SGLLinkGeneratorFormUI: React.FC<FormikProps<SGLLInkGenerator> & OuterProps> = (props) => {
  const { errors, values, setFieldValue, tickmillCompanies } = props

  const [landingPageOptions, setLandingPageOptions] = useState<PromoPage[]>([])

  const [domainOptions, setDomainOptions] = useState<DomainName[]>([])
  const [SGLLink, setSGLLink] = useState<string>('')

  const getAvailablePages = async ({
    entity,
    language,
  }: {
    entity?: string
    language?: string
  }) => {
    setFieldValue('landingPage', '')
    setFieldValue('domain', '')

    const availablePagesFetch = await props.getAvailablePages({ entity, language })
    if (availablePagesFetch.domainsFetch) setDomainOptions(availablePagesFetch.domainsFetch)
    if (availablePagesFetch.pagesFetch) setLandingPageOptions(availablePagesFetch.pagesFetch.items)
  }

  const generateUrl = async ({
    landingPage,
    domain,
    platform,
    language,
  }: {
    landingPage?: string
    domain?: string
    platform?: string
    language?: string
  }) => {
    const { sglLinkFetchUrl } = await props.generateUrl({ landingPage, domain, platform, language })
    if (sglLinkFetchUrl) setSGLLink(sglLinkFetchUrl)
  }

  const handlOnClickGenerate = (domainOptions: DomainName[]) => {
    const domainName = domainOptions.find((o) => o.id === values.domain)?.clientAreaDomain

    if (values.landingPage && domainName) {
      generateUrl({
        landingPage: values.landingPage,
        domain: domainName,
        platform: values.platform,
        language: values.language,
      })
    }
  }

  useEffect(() => {
    if (values.entity && values.language) {
      setSGLLink('')
      getAvailablePages({ entity: values.entity, language: values.language })
    }
  }, [values.entity, values.language])

  const isGenerateButtonDisabled = useMemo(() => {
    return props.isSubmitting || !values.domain || !values.landingPage
  }, [props.isSubmitting, values.domain, values.landingPage])
  const languageOptions = useMemo(
    () => mainWebsiteLanguages.map((language) => ({ id: language.value, name: language.label })),
    []
  )

  return (
    <div className='px-4 md:px-6'>
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              <Button
                disabled={isGenerateButtonDisabled}
                type='button'
                onClick={() => handlOnClickGenerate(domainOptions)}
              >
                Generate
              </Button>
              <Button
                disabled={!SGLLink}
                type='button'
                variant='outline'
                onClick={() => SGLLink && copy(SGLLink)}
              >
                Copy
              </Button>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard title='SGL Link Generator'>
              <FormSelectField
                name='entity'
                label='Entity'
                value={values.entity}
                options={tickmillCompanies}
                placeholder='Entity'
                allowClear
                showSearch
                error={errors.entity}
                onChange={(value) => setFieldValue('entity', value ?? '')}
              />

              <FormSelectField
                name='language'
                label='Language'
                value={values.language}
                options={languageOptions}
                placeholder='Language'
                allowClear
                showSearch
                error={errors.language}
                onChange={(value) => setFieldValue('language', value ?? '')}
              />

              <FormSelectField
                name='domain'
                label='Domain'
                value={values.domain}
                options={domainOptions}
                placeholder='Domain'
                allowClear
                showSearch
                disabled={domainOptions.length < 1}
                error={errors.domain}
                onChange={(value) => setFieldValue('domain', value ?? '')}
              />
              <FormSelectField
                name='landingPage'
                label='Landing Page'
                value={values.landingPage}
                options={landingPageOptions}
                placeholder='Landing Page'
                allowClear
                showSearch
                disabled={landingPageOptions.length < 1}
                error={errors.landingPage}
                onChange={(value) => setFieldValue('landingPage', value ?? '')}
              />
              <FormField name='platform' label='Platform' value={values.platform} />
              <FormReadonlyField name='sglLink' label='SGL Link' value={SGLLink} canCopy />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </div>
  )
}

interface OuterProps {
  sglLink?: SGLLInkGenerator
  tickmillCompanies: TickmillCompanyDto[]
  getAvailablePages({
    entity,
    language,
  }: {
    entity?: string
    language?: string
  }): Promise<{ pagesFetch?: PromoPageFetch; domainsFetch?: DomainName[] }>

  generateUrl({
    landingPage,
    domain,
    platform,
    language,
  }: {
    landingPage?: string
    domain?: string
    platform?: string
    language?: string
  }): Promise<{ sglLinkFetchUrl?: string }>
}

const SGLLinkGeneratorForm = withFormik<OuterProps, SGLLInkGenerator>({
  mapPropsToValues: ({ sglLink }) => {
    return {
      entity: sglLink?.entity ?? '',
      language: sglLink?.language ?? '',
      landingPage: sglLink?.landingPage ?? '',
      domain: sglLink?.domain,
      platform: sglLink?.platform ?? '',
    }
  },
  handleSubmit: () => {},
  enableReinitialize: true,
})(SGLLinkGeneratorFormUI)

export default SGLLinkGeneratorForm
